import {
  Button,
  Chip,
  CircularProgress,
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import AbsHistory from "../../abstract/History";
import { SortType } from "../../abstract/types";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { GetCycleResponse, GetCycleResponseItem } from "./types";
import { formatDate, scoreColor } from "../../utils";
import { perPage } from "../../constants";
import { useError } from "../../contexts/ErrorContext";
import { useWebSocket, WebSocketEvent } from "../../contexts/WebSocketContext";
import { useAuth } from "../../contexts/AuthContext";
import { colors, fontSizes } from "../../theme";

const History: React.FC = () => {
  const [data, setData] = useState<GetCycleResponseItem[]>([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [disabledId, setDisabledId] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(true);

  const { updateActions, updateBreadcrumbs } = useApp();
  const navigate = useNavigate();
  const { apiService } = useApi();
  const { showError, showSuccess } = useError();
  const location = useLocation();
  const { session } = useAuth();
  const websocket = useWebSocket();

  const newId = location.state?.newId;

  const theme = useTheme();

  const { translate } = useApp();
  const translations = useMemo(() => translate("oocyte"), [translate]);
  const generalTranslations = useMemo(() => translate("general"), [translate]);

  useEffect(() => {
    if (data?.length) {
      updateActions([
        <Button
          key={"create"}
          color="blackButton"
          onClick={() => navigate("/oocyte/create")}
        >
          {generalTranslations.history.buttons.addNewCycle}
        </Button>,
      ]);
    }
    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.oocyteQualityAssessment,
        onClick: () => {
          navigate("/oocyte");
        },
      },
    ]);

    return () => {
      updateActions([]);
      updateBreadcrumbs([]);
    };
  }, [data]);

  const handleOpenItem = (row: GetCycleResponseItem) => {
    navigate(`/oocyte/${row.id}`);
  };

  useEffect(() => {
    websocket.register("oocyte", handleEvent);
    return () => {
      websocket.unregister("oocyte", handleEvent);
    };
  }, [data]);

  const refetchItem = useCallback(
    async (id: string) => {
      console.log("Refetching item", id);
      if (!data) {
        return;
      }
      try {
        let cData = [...data];
        const cycle = await apiService.get<GetCycleResponse>(`/oocyte/${id}`);
        const index = cData.findIndex((c) => c.id === id);
        if (index !== -1) {
          cData[index] = cycle.cycle;
          setData(cData);
          setDisabledId(null);
        }
      } catch (error) {
        console.error(error);
        showError(translations.history.errors.errorFetchingData);
      } finally {
        setDisabledId(null);
      }
    },
    [data]
  );

  const handleEvent = useCallback(
    (event: WebSocketEvent) => {
      console.log("WebSocket oocyte event:", event.name, event.data.cycle);
      if (event.name === "cycle_done") {
        showSuccess(translations.history.messages.doneAnalyzingCycle);
        console.info("Done analyzing cycle, data:", data);
        refetchItem(event.data.cycle);
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, newId: undefined },
        });
      }
    },
    [showSuccess, navigate, location.pathname, location.state, data]
  );

  const get = useCallback(
    async (
      sort: SortType<
        GetCycleResponseItem & {
          actualOutcome: string;
          predictedBlastulationRate: string;
        }
      > | null = null,
      patient: string = "",
      review: boolean = false,
      pageToken?: string
    ) => {
      const params = new URLSearchParams();
      if (sort) {
        params.append("sort", `${sort.column},${sort.direction}`);
      }
      if (patient) {
        params.append("patient", patient);
      }
      if (review) {
        params.append("review", "1");
      }
      if (pageToken) {
        params.append("page_token", pageToken);
      }

      setIsSearching(true);

      try {
        const response = await apiService.get<{
          next_page_token: string | null;
          cycles: GetCycleResponseItem[];
        }>(`/oocyte?${params.toString()}`);

        if (response.next_page_token) {
          setNextPageToken(response.next_page_token);
        }

        return response.cycles;
      } catch (error) {
        console.error(error);
        setData([]);
        showError(translations.history.errors.errorFetchingData);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  const onSearch = (
    sort: SortType<
      GetCycleResponseItem & {
        actualOutcome: string;
        predictedBlastulationRate: string;
      }
    > | null,
    patient: string,
    review: boolean,
    nextPageToken?: string
  ) => {
    get(sort, patient, review, nextPageToken)
      .then((data) => {
        // setCount((prev) => prev + data.length);
        setData(data ?? []);
      })
      .catch((error) => {
        setData([]);
        showError(error);
      });
  };

  const visibleData = useMemo(() => {
    return data.slice(page * perPage, page * perPage + perPage);
  }, [page, perPage, data]);

  const getOutcomeText = (cycle: GetCycleResponseItem) => {
    const ret = [];
    if (cycle.u_fertilized) {
      ret.push(
        `${cycle.u_fertilized} ${translations.history.outcome.fertilizedOocytes}`
      );
    }
    if (cycle.u_blastocyst) {
      ret.push(
        `${cycle.u_blastocyst} ${translations.history.outcome.blastocysts}`
      );
    }
    if (cycle.u_livebirth) {
      ret.push(
        `${cycle.u_livebirth} ${translations.history.outcome.liveBirths}`
      );
    }
    if (ret.length) {
      return ret.join(", ");
    }

    return translations.history.outcome.reviewRequired;
  };

  useEffect(() => {
    if (newId && session) {
      setDisabledId(newId);

      const to = setTimeout(() => {
        if (disabledId !== null) {
          setDisabledId(null);
          // refetchItem(newId);
          navigate(location.pathname, {
            replace: true,
            state: { ...location.state, newId: undefined },
          });
          showSuccess(translations.history.messages.doneAnalyzingCycle);
          console.log("Done with timeout");
        }
      }, 60000);
      return () => clearTimeout(to);
    }
  }, [newId, session]);

  const calcPredictedBlastulationRate = (
    item: GetCycleResponseItem
  ): number | null => {
    const imgs = item.images.filter(
      (img) => img.p_blastulation !== null && img.p_blastulation !== undefined
    );
    console.info("Images with blastulation rate:", imgs);
    if (!imgs.length) {
      return null;
    }
    const rate =
      imgs.reduce((acc, img) => acc + img.p_blastulation, 0) / imgs.length;
    console.info("Predicted blastulation rate:", rate);
    return Math.round(rate);
  };

  const predictedBlastulationRate = (
    cycle: GetCycleResponseItem
  ): ReactElement | null => {
    if (disabledId === cycle.id) {
      return (
        <Chip
          sx={{
            backgroundColor: colors.stone[100],
            fontWeight: "normal",
            fontSize: fontSizes.sm,
            color: colors.stone[900],
          }}
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress
                size={"20px"}
                color="inherit"
                sx={{ color: colors.stone[900] }}
              />
              <Typography fontSize={fontSizes.sm} fontWeight={"bold"} color={colors.stone[900]}>
                Processing the result
              </Typography>
            </Stack>
          }
        />
      );
    }
    const value = calcPredictedBlastulationRate(cycle);
    console.log("Predicted blastulation rate:", value);
    if (value) {
      return (
        <Chip
          sx={{
            backgroundColor: scoreColor(value),
            fontWeight: "normal",
            fontSize: fontSizes.sm,
            color: colors.stone[900],
          }}
          label={`${value}%`}
        />
      );
    }
    return null;
  };

  return (
    <AbsHistory<
      GetCycleResponseItem & {
        actualOutcome: string;
        predictedBlastulationRate: string;
      }
    >
      isLoadingData={isSearching}
      disabledId={disabledId}
      pagintation={{
        page: page,
        perPage: perPage,
        total: count,
      }}
      onSearch={onSearch}
      title={translations.history.title}
      columns={[
        {
          id: "id",
          label: translations.history.columns.cycleId,
          sortable: true,
        },
        {
          id: "patient",
          label: translations.history.columns.patientName,
          sortable: true,
        },
        {
          id: "created",
          label: translations.history.columns.cycleDate,
          sortable: true,
        },
        {
          id: "predictedBlastulationRate",
          label: translations.history.columns.predictedBlastulationRate,
          width: 210,
        },
      ]}
      data={
        visibleData
          ? (visibleData.map((cycle) => ({
              ...cycle,
              created: formatDate(new Date(cycle.created)),
              actualOutcome: getOutcomeText(cycle),
              predictedBlastulationRate: predictedBlastulationRate(cycle),
            })) as any)
          : null
      }
      onItemClick={handleOpenItem}
    />
  );
};

export default History;
